import {
  afterNextRender,
  AfterViewInit,
  Component,
  computed,
  contentChild,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  output,
  Signal,
  signal,
  TemplateRef,
  untracked,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import {SelectFilterEvent, SelectLazyLoadEvent, SelectModule} from 'primeng/select';
import {Selectable} from '../../../../core/interfaces/selectable.interface';
import {AvailablePaginationSize} from '../../../../core/types/available-pagination-size.type';
import {Page} from '../../../../core/models/page-response.model';
import {NgTemplateOutlet} from '@angular/common';
import {Subject} from 'rxjs';
import {debounceTime, takeUntil} from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ValidationErrorsDictionary} from '../../../../core/dictionary/validation-errors.dictionary';

export type SelectVariant = 'outlined' | 'filled';
export type SelectLabelMode = 'fixed' | 'float' | 'ifta';
export type SelectSize = 'small' | 'large' | undefined;

export interface SelectChangeEvent<T extends Selectable<any>> {
  originalEvent: Event;
  value: T['value'];
}

export interface SelectLoadMoreEvent {
  page: number;
  size: AvailablePaginationSize;
  query: string | null;
}

@Component({
  selector: 'app-select-input',
  imports: [ReactiveFormsModule, SelectModule, NgTemplateOutlet, FormsModule],
  templateUrl: './select-input.component.html',
  styleUrl: './select-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectInputComponent,
      multi: true,
    },
  ],
})
export class SelectInputComponent<T extends Selectable<TValue>, TValue = T['value']>
  implements ControlValueAccessor, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  private readonly destroy$ = new Subject<void>();
  private readonly filterSubject$ = new Subject<string | null>();

  readonly options = input.required<Page<T> | T[] | null>();
  readonly optionGroupLabel = input<string>('label');
  readonly optionGroupChildren = input<string>('items');
  readonly group = input<boolean>(false);

  readonly label = input<string>('');
  readonly placeholder = input<string>('');
  readonly variant = input<SelectVariant>('outlined');
  readonly labelMode = input<SelectLabelMode>('fixed');
  readonly size = input<SelectSize>(undefined);
  readonly checkmark = input<boolean>(false);
  readonly showClear = input<boolean>(false);

  readonly disabled = input<boolean>(false);
  readonly editable = input<boolean>(false);

  readonly lazy = input<boolean>(false);

  readonly filter = input<boolean>(false);
  readonly filterPlaceholder = input<string>('Szukaj');
  readonly filterMatchMode = input<'contains' | 'startsWith' | 'endsWith' | 'equals' | 'notEquals' | 'in' | 'lt' | 'lte' | 'gt' | 'gte'>('contains');

  readonly filterDebounceTime = input<number>(300);

  readonly focusOnHover = input<boolean>(true);
  readonly selectOnFocus = input<boolean>(false);
  readonly autoOptionFocus = input<boolean>(false);

  readonly virtualScroll = input<boolean>(false);
  readonly showLoading = input<boolean>(false);
  readonly lazyPageSize = input<AvailablePaginationSize>(10);

  readonly showErrorMessage = input<boolean>(true);

  readonly selectionChange = output<SelectChangeEvent<T>>();
  readonly onFilter = output();
  readonly onShow = output();
  readonly onHide = output();
  readonly onFocus = output();
  readonly onBlur = output();
  readonly onClear = output();
  readonly onLoadMore = output<SelectLoadMoreEvent>();

  readonly optionTemplate = contentChild<TemplateRef<any>>('optionTemplate');
  readonly selectedOptionTemplate = contentChild<TemplateRef<any>>('selectedOptionTemplate');
  readonly groupTemplate = contentChild<TemplateRef<any>>('groupTemplate');
  readonly headerTemplate = contentChild<TemplateRef<any>>('headerTemplate');
  readonly footerTemplate = contentChild<TemplateRef<any>>('footerTemplate');
  readonly emptyTemplate = contentChild<TemplateRef<any>>('emptyTemplate');
  readonly loaderTemplate = contentChild<TemplateRef<any>>('loaderTemplate');
  readonly filterTemplate = contentChild<TemplateRef<any>>('filterTemplate');
  readonly clearIconTemplate = contentChild<TemplateRef<any>>('clearIconTemplate');
  readonly dropdownIconTemplate = contentChild<TemplateRef<any>>('dropdownIconTemplate');

  protected readonly isClosed = signal(false);
  protected readonly isDisabled = signal(false);
  protected readonly isLoading = signal<boolean>(false);
  protected readonly controlState = signal<{
    invalid: boolean;
    touched: boolean;
    dirty: boolean;
    errors: ValidationErrors | null;
  }>({
    invalid: false,
    touched: false,
    dirty: false,
    errors: null,
  });
  protected readonly selected = signal<TValue | null>(null);
  protected readonly filterQuery = signal<string | null>(null);
  protected readonly availableOptions = signal<T[]>([]);

  private readonly totalElements = signal<number | null>(null);

  protected readonly VIRTUAL_SCROLL_ITEM_SIZE: number = 45;

  protected readonly OPTION_LABEL: string = 'displayName';
  protected readonly OPTION_VALUE: string = 'value';
  protected readonly OPTION_DISABLED: string = 'disabled';
  protected readonly FILTER_BY: string = 'displayName';

  protected readonly filterEnabled = computed(() => this.filter() && !this.lazy());

  protected readonly isInvalid = computed(() => {
    const state = this.controlState();
    return state.invalid && (state.touched || state.dirty);
  });

  protected readonly errorMessage = computed(() => {
    const state = this.controlState();
    if (!state.errors) return null;
    return ValidationErrorsDictionary.resolveValidationErrors(state.errors);
  });

  protected readonly useFloatLabel = computed(() => this.labelMode() === 'float');
  protected readonly useIftaLabel = computed(() => this.labelMode() === 'ifta');
  protected readonly hasRequiredRule: Signal<boolean> = computed(() => {
    const state = this.controlState();
    return state.errors?.['required'] !== undefined;
  });

  protected ngControl: NgControl | null = null;

  constructor() {
    this.optionsTypeStrategy();
    this.initFilterDebounce();
    this.initLazyLoad();
    this.initCleanup();
    this.registerLoadingStateChanges();
  }

  public ngAfterViewInit(): void {
    this.handleNgControl();
  }

  public writeValue(value: TValue | null): void {
    this.selected.set(value);
  }

  public registerOnChange(fn: (value: TValue | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  protected handleShow(): void {
    this.onShow.emit();
  }

  protected handleHide(): void {
    this.onTouched();
    this.isClosed.set(true);
    this.onHide.emit();
    this.updateControlState();
  }

  protected handleLazyLoad($event: SelectLazyLoadEvent): void {
    if (this.isLoading()) return;

    const pageSize = this.lazyPageSize();
    const currentLength = this.availableOptions().length;
    const total = this.totalElements();

    if (total !== null && currentLength >= total) return;

    if ($event.last >= currentLength - 1) {
      const nextPage = Math.floor(currentLength / pageSize);

      this.isLoading.set(true);

      this.onLoadMore.emit({
        page: nextPage,
        size: pageSize,
        query: this.filterQuery(),
      });
    }
  }

  protected handleClear(): void {
    this.onClear.emit();
  }

  protected handleBlur($event: any): void {
    this.onBlur.emit();
    this.onTouched();
  }

  protected handleFocus($event: any): void {
    this.onFocus.emit();
  }

  protected handleFilter($event: SelectFilterEvent): void {
    const query = $event.filter?.trim() || null;

    if (this.lazy()) {
      this.filterSubject$.next(query);
    } else {
      this.filterQuery.set(query);
    }
  }

  protected handleChange($event: any): void {
    this.onChange($event.value);
    this.selected.set($event.value);
    this.updateControlState();
  }

  private onChange: (value: TValue | null) => void = () => {
  };

  private onTouched: () => void = () => {
  };

  private handleNgControl(): void {
    try {
      this.ngControl = this.injector.get(NgControl, null, {
        optional: true,
        self: true,
      });

      if (this.ngControl?.control) {
        const ctrl = this.ngControl.control;

        ctrl.statusChanges?.pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.updateControlState();
          });

        this.updateControlState();
      }
    } catch (e) {
      console.error(`[${SelectInputComponent.name}] Could not find NgControl in parent injector tree. Make sure that you're using the component inside a form field. Error: ${e}`);
    }
  }

  private initCleanup(): void {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }

  private initFilterDebounce(): void {
    this.filterSubject$
      .pipe(
        debounceTime(this.filterDebounceTime()),
        takeUntil(this.destroy$),
      )
      .subscribe((query) => {
        if (!this.lazy()) return;

        this.filterQuery.set(query);
        this.availableOptions.set([]);
        this.totalElements.set(null);
        this.isLoading.set(true);

        this.onLoadMore.emit({
          page: 0,
          size: this.lazyPageSize(),
          query: query,
        });
      });
  }

  private initLazyLoad(): void {
    afterNextRender(() => {
      if (this.lazy()) {
        this.isLoading.set(true);
        this.onLoadMore.emit({
          page: 0,
          size: this.lazyPageSize(),
          query: null,
        });
      }
    });
  }

  private optionsTypeStrategy(): void {
    effect((): void => {
      const options = this.options();

      this.isLoading.set(false);

      if (!options) return;

      untracked(() => {
        if (options instanceof Page) {
          this.handlePageableOptions(options);
        } else if (Array.isArray(options)) {
          if (options.length > 0 && options[0]?.toSelectText === undefined) {
            throw new Error(`[${SelectInputComponent.name}] Model entities must implement Selectable interface`);
          }
          this.availableOptions.set(options);
        }
      });
    }, {allowSignalWrites: true});
  }

  private handlePageableOptions(pageableOptions: Page<T>): void {
    const {content, number, size, totalElements} = pageableOptions;

    this.totalElements.set(totalElements);

    if (number === 0) {
      this.availableOptions.set([...content]);
      return;
    }

    const current = this.availableOptions();
    const expectedLength = (number + 1) * size;

    if (current.length >= expectedLength) return;

    const existingValues = new Set(current.map(o => o.value));
    const newItems = content.filter(o => !existingValues.has(o.value));

    this.availableOptions.set([...current, ...newItems]);
  }

  private updateControlState(): void {
    if (this.ngControl?.control) {
      const ctrl = this.ngControl.control;
      this.controlState.set({
        invalid: ctrl.invalid,
        touched: ctrl.touched,
        dirty: ctrl.dirty,
        errors: ctrl.errors,
      });
    }
  }

  private registerLoadingStateChanges(): void {
    effect(() => {
      this.isLoading.set(this.showLoading());
    });
  }
}
